/**
 * FilePreviewDialog — inline file preview, rename, delete, download, and rotate.
 *
 * Extracted from HospitalityDetailDialog (Stage 3 UI refactor).
 * Can be reused by any component that needs file preview functionality.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Pencil, Trash2, Download, FileText, RotateCw } from 'lucide-react';

const FilePreviewDialog = ({
  open,
  onOpenChange,
  previewFileUrl,
  previewFileType,
  isEditingFile,
  setIsEditingFile,
  editFileDescription,
  setEditFileDescription,
  updatingFile,
  onDeleteFile,
  onUpdateDescription,
  onDownload,
  onResetDescription, // called when user cancels the rename — restores original name
  onRotate,           // optional: async () => void — rotates image and re-uploads
  isRotating,         // optional: boolean — shows spinner while rotating
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl" data-testid="hosp-file-preview-dialog">
      <DialogHeader className="flex flex-row items-center justify-between">
        {isEditingFile ? (
          <div className="flex-1 mr-4">
            <Input
              value={editFileDescription}
              onChange={(e) => setEditFileDescription(e.target.value)}
              placeholder="Enter file name/description"
              autoFocus
            />
          </div>
        ) : (
          <DialogTitle className="flex-1">{editFileDescription || 'File Preview'}</DialogTitle>
        )}
      </DialogHeader>

      <div className="flex items-center justify-center p-4 bg-black/5 rounded min-h-[200px]">
        {previewFileUrl && (
          <>
            {previewFileType?.startsWith('image/') && (
              <img src={previewFileUrl} alt="Preview" className="max-w-full max-h-[60vh] object-contain" />
            )}
            {previewFileType === 'application/pdf' && (
              <iframe src={previewFileUrl} className="w-full h-[60vh]" title="PDF Preview" />
            )}
            {!previewFileType?.startsWith('image/') && previewFileType !== 'application/pdf' && (
              <div className="text-center py-8">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">Preview not available for this file type.</p>
              </div>
            )}
          </>
        )}
      </div>

      <DialogFooter className="flex justify-between sm:justify-between">
        <div className="flex gap-2">
          {isEditingFile ? (
            <>
              <Button variant="outline" onClick={onResetDescription} disabled={updatingFile}>Cancel</Button>
              <Button onClick={onUpdateDescription} disabled={updatingFile || !editFileDescription.trim()}>
                {updatingFile ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="icon" onClick={() => setIsEditingFile(true)} title="Edit name">
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={onDeleteFile}
                title="Delete file"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              {/* Rotate button — only shown for image files when onRotate handler is provided */}
              {onRotate && previewFileType?.startsWith('image/') && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={onRotate}
                  disabled={!!isRotating}
                  title="Rotate 90° clockwise"
                  data-testid="rotate-hosp-photo-btn"
                >
                  <RotateCw className={`w-4 h-4 ${isRotating ? 'animate-spin' : ''}`} />
                </Button>
              )}
            </>
          )}
        </div>
        {!isEditingFile && (
          <Button onClick={onDownload} disabled={!previewFileUrl}>
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default FilePreviewDialog;
